import { Action } from 'redux';
import { combineEpics, Epic } from 'redux-observable';
import { of, withLatestFrom } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ofType } from 'ts-action-operators';
import { loadProjectsSpecific } from './actions';
import { RootState } from '../root-state.type';
import { TSuggestions } from '../../types';
import {
  fetchCurrentProjects,
  fetchFavouriteProjects,
  fetchForestationProjects,
  fetchMangroveProjects,
  fetchMangroveAggregations,
  fetchMangroveDistricts,
} from '../../pages/Home/store-utils';

interface ProjectFetchParams {
  skip?: number;
  limit?: number;
  search?: null;
}

const createCommonProps = (
  location: { level: number; id: string; name: string } | null,
): TSuggestions => {
  if (!location) {
    return { suggestions: [] };
  }
  return {
    suggestions: [
      {
        id: location.id,
        level: location.level,
        name: location.name,
      },
    ],
  };
};

const createFilterProps = (
  filters: any,
  page: number,
  perPage: number,
  search: string,
): Record<string, any> => {
  const fCopy = { ...filters, skip: page - 1, limit: perPage };
  const filterProps = Object.keys(fCopy).reduce((acc: any, curr) => {
    if (fCopy[curr] !== 'None' && !!fCopy[curr]) {
      acc[curr] = fCopy[curr];
    }
    return acc;
  }, {});

  if (search) {
    filterProps.search = search;
  }

  return filterProps;
};

const handleCurrentProjects = (
  commonProps: TSuggestions,
  filterProps: Record<string, any>,
): void => {
  fetchCurrentProjects(commonProps, filterProps);
};

const handleRecommendationProjects = (
  selectedRecommendationType: string,
  mangroveViewMode: 'districts' | 'aggregations',
  location: { level: number; id: string; name: string } | null,
  filterProps: Record<string, any>,
  page: number,
  perPage: number,
  recommendationPagination: any,
): void => {
  console.log('EPIC DEBUG - handleRecommendationProjects called with:', {
    selectedRecommendationType,
    mangroveViewMode,
    location,
    filterProps,
  });

  if (selectedRecommendationType === 'Mangroves') {
    console.log('EPIC DEBUG - Fetching mangrove projects');
    if (mangroveViewMode === 'districts') {
      fetchMangroveDistricts(location, filterProps);
    } else {
      fetchMangroveAggregations(location, filterProps);
    }
  } else if (selectedRecommendationType === 'Forests') {
    console.log('EPIC DEBUG - Fetching forest projects');
    const params: ProjectFetchParams = {
      skip: page - 1,
      limit: perPage,
      search: null,
    };
    fetchForestationProjects(params);
  } else {
    console.log(
      'EPIC DEBUG - Default case - treating as forest recommendations, selectedRecommendationType:',
      selectedRecommendationType,
    );
    // Default case - treat as forest recommendations
    const params: ProjectFetchParams = {
      skip: page - 1,
      limit: perPage,
      search: null,
    };
    fetchForestationProjects(params);
  }
};

const handleSavedProjects = (page: number, perPage: number): void => {
  fetchFavouriteProjects({ skip: page - 1, limit: perPage });
};

const loadProjectsEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(loadProjectsSpecific.submit),
    withLatestFrom(state$),
    switchMap(([, state]: [Action, RootState]) => {
      const {
        selected,
        selectedRecommendationType,
        mangroveViewMode,
        filters,
        search: location,
        searchByNaming: search,
        pagination: { page, perPage },
        recommendationPagination,
      } = state.projects;

      const commonProps = createCommonProps(location);
      const filterProps = createFilterProps(filters, page, perPage, search);

      switch (selected) {
        case 'current':
          handleCurrentProjects(commonProps, filterProps);
          break;
        case 'recommendation':
          handleRecommendationProjects(
            selectedRecommendationType || 'Mangroves',
            mangroveViewMode,
            location,
            filterProps,
            page,
            perPage,
            recommendationPagination,
          );
          break;
        case 'saved':
          handleSavedProjects(page, perPage);
          break;
      }

      return of(loadProjectsSpecific.success({}));
    }),
    catchError((error) => {
      // Use proper error logging instead of console.error
      return of(
        loadProjectsSpecific.fail(error.message || 'An error occurred while loading projects'),
      );
    }),
  );

export const projectsEpics = combineEpics(loadProjectsEpic);
